import { useState, useEffect } from 'react';
import { sharepointAPI } from '../lib/api';
import { FiFolder, FiFolderPlus, FiLoader, FiChevronDown, FiChevronRight, FiCheck, FiX, FiSearch, FiAlertCircle } from 'react-icons/fi';

// Recursive folder component
function FolderItem({ folder, drive, site, level, onToggle, onSelect, isSelected, isExpanded, contents, loading, folderPath = '' }) {
  const folderKey = `${site.id}:${drive.id}:${folder.id}`;
  const paddingLeft = `${12 + (level * 20)}px`;
  const hasSubfolders = folder.folder?.childCount > 0;

  // Build the current folder's path
  const currentFolderPath = folderPath ? `${folderPath}/${folder.name}` : folder.name;

  return (
    <div>
      {/* Folder Header */}
      <div className="flex items-center p-4 hover:bg-gray-200" style={{ paddingLeft }}>
        <div
          className="mr-2 text-gray-600 cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            if (hasSubfolders) {
              onToggle(folder, drive, site);
            }
          }}
        >
          {hasSubfolders ? (
            isExpanded[folderKey] ? (
              <FiChevronDown className="w-4 h-4" />
            ) : (
              <FiChevronRight className="w-4 h-4" />
            )
          ) : (
            <div className="w-4 h-4" />
          )}
        </div>
        <div
          className="flex items-center flex-1 cursor-pointer"
          onClick={() => onSelect(site, drive, folder, currentFolderPath)}
        >
          <div
            className={`w-5 h-5 mr-3 border-2 rounded flex items-center justify-center transition-colors ${
              isSelected(site, drive, folder)
                ? 'bg-blue-600 border-blue-600'
                : 'border-gray-300'
            }`}
          >
            {isSelected(site, drive, folder) && (
              <FiCheck className="w-3 h-3 text-white" />
            )}
          </div>
          <FiFolder className="w-5 h-5 text-yellow-600 mr-3" />
          <div className="flex-1">
            <h4 className="font-medium text-gray-900">{folder.name}</h4>
          </div>
        </div>
      </div>

      {/* Subfolders */}
      {hasSubfolders && isExpanded[folderKey] && (
        <div>
          {loading[folderKey] ? (
            <div className="flex justify-center items-center py-4" style={{ paddingLeft }}>
              <FiLoader className="w-4 h-4 animate-spin text-blue-600" />
            </div>
          ) : (
            <div>
              {(contents[folderKey] || [])
                .filter(item => item.folder)
                .map((subfolder) => (
                  <FolderItem
                    key={subfolder.id}
                    folder={subfolder}
                    drive={drive}
                    site={site}
                    level={level + 1}
                    onToggle={onToggle}
                    onSelect={onSelect}
                    isSelected={isSelected}
                    isExpanded={isExpanded}
                    contents={contents}
                    loading={loading}
                    folderPath={currentFolderPath}
                  />
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function SharePointBrowser({ connection, onComplete, onCancel }) {
  const [sites, setSites] = useState([]);
  const [expandedSites, setExpandedSites] = useState({});
  const [siteDrives, setSiteDrives] = useState({});
  const [expandedDrives, setExpandedDrives] = useState({});
  const [driveContents, setDriveContents] = useState({});
  const [expandedFolders, setExpandedFolders] = useState({});
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingDrives, setLoadingDrives] = useState({});
  const [loadingFolders, setLoadingFolders] = useState({});
  const [syncing, setSyncing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);
  const [processingSelection, setProcessingSelection] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [processingProgress, setProcessingProgress] = useState(0);
  const [processingTotal, setProcessingTotal] = useState(0);
  const [loadingPreviousSelections, setLoadingPreviousSelections] = useState(false);

  useEffect(() => {
    loadSites();

    // Pre-populate selected items from existing connection
    if (connection.connected_sites && connection.connected_sites.length > 0) {
      const existingSelections = connection.connected_sites.map(site => {
        const itemKey = site.folder_id
          ? `${site.site_id}:${site.drive_id}:${site.folder_id}`
          : `${site.site_id}:${site.drive_id}`;

        const item = {
          key: itemKey,
          siteId: site.site_id,
          siteName: site.site_name,
          driveId: site.drive_id,
          driveName: site.drive_name,
        };

        if (site.folder_id) {
          item.folderId = site.folder_id;
          item.folderName = site.folder_name;
          item.folderPath = site.folder_path;
        }

        return item;
      });
      setSelectedItems(existingSelections);
    }
  }, [connection.id]);

  // Auto-expand sites and drives that have selected items
  useEffect(() => {
    const autoExpandSelections = async () => {
      if (!sites.length || !selectedItems.length) return;

      console.log('Auto-expanding selections:', selectedItems);
      setLoadingPreviousSelections(true);

      // Group selected items by site and drive
      const itemsBySite = {};
      selectedItems.forEach(item => {
        if (!itemsBySite[item.siteId]) {
          itemsBySite[item.siteId] = {
            drives: {},
          };
        }
        if (!itemsBySite[item.siteId].drives[item.driveId]) {
          itemsBySite[item.siteId].drives[item.driveId] = [];
        }
        if (item.folderId) {
          itemsBySite[item.siteId].drives[item.driveId].push(item);
        }
      });

      // Expand sites that have selections
      const newExpandedSites = { ...expandedSites };
      const newExpandedDrives = { ...expandedDrives };
      const newExpandedFolders = { ...expandedFolders };
      const newSiteDrives = { ...siteDrives };
      const newDriveContents = { ...driveContents };
      const newLoadingDrives = { ...loadingDrives };
      const newLoadingFolders = { ...loadingFolders };

      for (const siteId of Object.keys(itemsBySite)) {
        const site = sites.find(s => s.id === siteId);
        if (!site) continue;

        // Expand site
        newExpandedSites[siteId] = true;

        // Load drives if not already loaded
        if (!siteDrives[siteId]) {
          try {
            newLoadingDrives[siteId] = true;
            const response = await sharepointAPI.getDrives(connection.id, siteId);
            newSiteDrives[siteId] = response.data.drives || [];
          } catch (err) {
            console.error('Failed to load drives for auto-expansion:', err);
            continue;
          } finally {
            newLoadingDrives[siteId] = false;
          }
        }

        // Expand drives that have selections
        const drives = newSiteDrives[siteId] || siteDrives[siteId] || [];
        for (const driveId of Object.keys(itemsBySite[siteId].drives)) {
          const drive = drives.find(d => d.id === driveId);
          if (!drive) continue;

          const driveKey = `${siteId}:${driveId}`;
          newExpandedDrives[driveKey] = true;

          // Load root folder contents if not already loaded
          if (!driveContents[driveKey]) {
            try {
              newLoadingFolders[driveKey] = true;
              const response = await sharepointAPI.getFolderContents(connection.id, driveId);
              newDriveContents[driveKey] = response.data.items || [];
            } catch (err) {
              console.error('Failed to load folder contents for auto-expansion:', err);
            } finally {
              newLoadingFolders[driveKey] = false;
            }
          }

          // For each selected folder in this drive, expand parent folders
          const selectedFolders = itemsBySite[siteId].drives[driveId];
          for (const selectedFolder of selectedFolders) {
            if (!selectedFolder.folderPath) continue;

            // Clean the folder path - remove Graph API path if present
            let cleanPath = selectedFolder.folderPath;
            // If path starts with /drives/, extract just the folder path after root:
            if (cleanPath.startsWith('/drives/')) {
              const rootIndex = cleanPath.indexOf('/root:');
              if (rootIndex !== -1) {
                cleanPath = cleanPath.substring(rootIndex + 6); // Skip '/root:'
                if (cleanPath.startsWith('/')) {
                  cleanPath = cleanPath.substring(1); // Remove leading slash
                }
              }
            }

            console.log(`Cleaned path from "${selectedFolder.folderPath}" to "${cleanPath}"`);

            // Skip if path is empty after cleaning
            if (!cleanPath) continue;

            // Parse folder path to get parent folders
            const pathParts = cleanPath.split('/');

            // Load contents for each level of the path
            let currentContents = newDriveContents[driveKey] || driveContents[driveKey] || [];
            console.log(`Processing path: ${selectedFolder.folderPath}, pathParts:`, pathParts);
            for (let i = 0; i < pathParts.length; i++) {
              const folderName = pathParts[i];
              const folder = currentContents.find(item => item.folder && item.name === folderName);

              console.log(`Looking for folder "${folderName}" in contents:`, currentContents.map(c => c.name));
              if (!folder) {
                console.log(`Folder "${folderName}" not found, breaking`);
                break;
              }
              console.log(`Found folder:`, folder);

              const folderKey = `${siteId}:${driveId}:${folder.id}`;

              // Expand this folder (unless it's the last one - the actual selected folder)
              if (i < pathParts.length - 1) {
                newExpandedFolders[folderKey] = true;

                // Load contents if not already loaded
                if (!newDriveContents[folderKey] && !driveContents[folderKey]) {
                  try {
                    newLoadingFolders[folderKey] = true;
                    const response = await sharepointAPI.getFolderContents(connection.id, driveId, folder.id);
                    newDriveContents[folderKey] = response.data.items || [];
                    currentContents = newDriveContents[folderKey];
                  } catch (err) {
                    console.error('Failed to load subfolder contents for auto-expansion:', err);
                    break;
                  } finally {
                    newLoadingFolders[folderKey] = false;
                  }
                } else {
                  currentContents = newDriveContents[folderKey] || driveContents[folderKey] || [];
                }
              }
            }
          }
        }
      }

      // Update all states
      setExpandedSites(newExpandedSites);
      setExpandedDrives(newExpandedDrives);
      setExpandedFolders(newExpandedFolders);
      setSiteDrives(newSiteDrives);
      setDriveContents(newDriveContents);
      setLoadingDrives(newLoadingDrives);
      setLoadingFolders(newLoadingFolders);
      setLoadingPreviousSelections(false);
    };

    autoExpandSelections();
  }, [sites, selectedItems.length]);

  const loadSites = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await sharepointAPI.getSites(connection.id, searchQuery);
      setSites(response.data.sites || []);
    } catch (err) {
      console.error('Failed to load sites:', err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadSites();
  };

  const toggleSite = async (site) => {
    const siteId = site.id;
    const isExpanded = expandedSites[siteId];

    if (isExpanded) {
      // Collapse
      setExpandedSites({ ...expandedSites, [siteId]: false });
    } else {
      // Expand and load drives
      setExpandedSites({ ...expandedSites, [siteId]: true });

      // Load drives if not already loaded
      if (!siteDrives[siteId]) {
        try {
          setLoadingDrives({ ...loadingDrives, [siteId]: true });
          const response = await sharepointAPI.getDrives(connection.id, siteId);
          setSiteDrives({ ...siteDrives, [siteId]: response.data.drives || [] });
        } catch (err) {
          console.error('Failed to load drives:', err);
          setError(err.response?.data?.error || err.message);
        } finally {
          setLoadingDrives({ ...loadingDrives, [siteId]: false });
        }
      }
    }
  };

  const toggleDrive = async (drive, site) => {
    const driveKey = `${site.id}:${drive.id}`;
    const isExpanded = expandedDrives[driveKey];

    if (isExpanded) {
      // Collapse
      setExpandedDrives({ ...expandedDrives, [driveKey]: false });
    } else {
      // Expand and load folder contents
      setExpandedDrives({ ...expandedDrives, [driveKey]: true });

      // Load root folder contents if not already loaded
      if (!driveContents[driveKey]) {
        try {
          setLoadingFolders({ ...loadingFolders, [driveKey]: true });
          const response = await sharepointAPI.getFolderContents(connection.id, drive.id);
          setDriveContents({ ...driveContents, [driveKey]: response.data.items || [] });
        } catch (err) {
          console.error('Failed to load folder contents:', err);
          setError(err.response?.data?.error || err.message);
        } finally {
          setLoadingFolders({ ...loadingFolders, [driveKey]: false });
        }
      }
    }
  };

  const toggleFolder = async (folder, drive, site, parentPath = '') => {
    const folderKey = `${site.id}:${drive.id}:${folder.id}`;
    const isExpanded = expandedFolders[folderKey];

    if (isExpanded) {
      // Collapse
      setExpandedFolders({ ...expandedFolders, [folderKey]: false });
    } else {
      // Expand and load subfolder contents
      setExpandedFolders({ ...expandedFolders, [folderKey]: true });

      // Load subfolder contents if not already loaded
      if (!driveContents[folderKey]) {
        try {
          setLoadingFolders({ ...loadingFolders, [folderKey]: true });
          const response = await sharepointAPI.getFolderContents(connection.id, drive.id, folder.id);
          setDriveContents({ ...driveContents, [folderKey]: response.data.items || [] });
        } catch (err) {
          console.error('Failed to load subfolder contents:', err);
          setError(err.response?.data?.error || err.message);
        } finally {
          setLoadingFolders({ ...loadingFolders, [folderKey]: false });
        }
      }
    }
  };

  // Helper function to recursively collect all child folders (loading them if needed)
  const collectAllChildFolders = async (site, drive, parentFolder, parentPath = '', updateProgress = null, currentDriveContents = null, progressTracker = null) => {
    const childFolders = [];
    const folderKey = `${site.id}:${drive.id}:${parentFolder.id}`;

    console.log(`[collectAllChildFolders] Processing: ${parentPath || parentFolder.name}, folderKey: ${folderKey}`);

    // Use the passed driveContents or the state
    const contentsState = currentDriveContents || driveContents;

    // Load folder contents if not already loaded
    let folderContents = contentsState[folderKey];
    console.log(`[collectAllChildFolders] Folder contents in state:`, folderContents ? `${folderContents.length} items` : 'not loaded');

    if (!folderContents) {
      try {
        setLoadingFolders(prev => ({ ...prev, [folderKey]: true }));
        if (updateProgress) {
          updateProgress(`Loading: ${parentPath || parentFolder.name}...`);
        }
        console.log(`[collectAllChildFolders] Fetching contents for: ${parentPath || parentFolder.name}`);
        const response = await sharepointAPI.getFolderContents(connection.id, drive.id, parentFolder.id);
        folderContents = response.data.items || [];
        console.log(`[collectAllChildFolders] Fetched ${folderContents.length} items`);

        // Update state and track in local variable for recursion
        setDriveContents(prev => {
          const updated = { ...prev, [folderKey]: folderContents };
          return updated;
        });

        // Update the currentDriveContents for this recursion path
        if (!currentDriveContents) {
          currentDriveContents = { ...driveContents };
        }
        currentDriveContents[folderKey] = folderContents;
      } catch (err) {
        console.error('Failed to load folder contents:', err);
        folderContents = [];
      } finally {
        setLoadingFolders(prev => ({ ...prev, [folderKey]: false }));
      }
    }

    // Get all subfolders in this folder
    const subfolders = folderContents.filter(item => item.folder);
    console.log(`[collectAllChildFolders] Found ${subfolders.length} subfolders in ${parentPath || parentFolder.name}:`, subfolders.map(f => f.name));

    for (const subfolder of subfolders) {
      const subfolderPath = parentPath ? `${parentPath}/${subfolder.name}` : subfolder.name;

      // Add this subfolder
      childFolders.push({
        key: `${site.id}:${drive.id}:${subfolder.id}`,
        siteId: site.id,
        siteName: site.displayName || site.name,
        driveId: drive.id,
        driveName: drive.name,
        folderId: subfolder.id,
        folderName: subfolder.name,
        folderPath: subfolderPath,
      });

      // Update progress
      if (progressTracker) {
        progressTracker.processed++;
        setProcessingProgress(progressTracker.processed);
      }

      // Recursively collect children of this subfolder
      const recursiveChildren = await collectAllChildFolders(site, drive, subfolder, subfolderPath, updateProgress, currentDriveContents, progressTracker);
      childFolders.push(...recursiveChildren);
    }

    console.log(`[collectAllChildFolders] Total child folders collected from ${parentPath || parentFolder.name}: ${childFolders.length}`);
    return childFolders;
  };

  const toggleSelection = async (site, drive, folder = null, folderPath = '') => {
    const itemKey = folder
      ? `${site.id}:${drive.id}:${folder.id}`
      : `${site.id}:${drive.id}`;
    const existing = selectedItems.find(item => item.key === itemKey);

    if (existing) {
      // Deselect this item and all its children
      setProcessingSelection(true);
      setProcessingMessage('Processing folder structure...');
      setProcessingProgress(0);
      setProcessingTotal(0);

      // Small delay to show the message
      await new Promise(resolve => setTimeout(resolve, 100));

      const keysToRemove = [itemKey];

      if (folder) {
        // Collect all child folder keys
        const updateProgress = (msg) => setProcessingMessage(msg);
        const progressTracker = { processed: 0 };
        const childFolders = await collectAllChildFolders(site, drive, folder, folderPath, updateProgress, null, progressTracker);
        keysToRemove.push(...childFolders.map(f => f.key));
        setProcessingMessage(`Deselecting ${childFolders.length + 1} folder${childFolders.length > 0 ? 's' : ''}...`);
        setProcessingTotal(childFolders.length);
      } else {
        // If deselecting a drive, remove all folders under this drive
        const foldersToRemove = selectedItems
          .filter(item => item.siteId === site.id && item.driveId === drive.id && item.folderId);
        keysToRemove.push(...foldersToRemove.map(item => item.key));
        setProcessingMessage(`Deselecting drive and ${foldersToRemove.length} folder${foldersToRemove.length !== 1 ? 's' : ''}...`);
        setProcessingTotal(foldersToRemove.length);
      }

      // Small delay to show the count
      await new Promise(resolve => setTimeout(resolve, 500));

      setSelectedItems(selectedItems.filter(item => !keysToRemove.includes(item.key)));
      setProcessingSelection(false);
      setProcessingMessage('');
      setProcessingProgress(0);
      setProcessingTotal(0);
    } else {
      // Select this item
      setProcessingSelection(true);
      setProcessingMessage('Processing folder structure...');
      setProcessingProgress(0);
      setProcessingTotal(0);

      // Small delay to show the message
      await new Promise(resolve => setTimeout(resolve, 100));

      const newItem = {
        key: itemKey,
        siteId: site.id,
        siteName: site.displayName || site.name,
        driveId: drive.id,
        driveName: drive.name,
      };

      if (folder) {
        newItem.folderId = folder.id;
        newItem.folderName = folder.name;
        newItem.folderPath = folderPath;
      }

      // Collect all child folders that should also be selected
      const updateProgress = (msg) => setProcessingMessage(msg);
      const progressTracker = { processed: 0 };
      let childrenToAdd = [];

      if (folder) {
        // Selecting a specific folder - collect its children
        childrenToAdd = await collectAllChildFolders(site, drive, folder, folderPath, updateProgress, null, progressTracker);

        // Auto-expand this folder to show its children
        if (childrenToAdd.length > 0) {
          const folderKey = `${site.id}:${drive.id}:${folder.id}`;
          setExpandedFolders(prev => ({ ...prev, [folderKey]: true }));
        }
      } else {
        // Selecting entire drive - collect ALL folders in the drive
        setProcessingMessage('Loading drive contents...');

        // First, load root-level folders if not already loaded
        const driveKey = `${site.id}:${drive.id}`;
        let rootContents = driveContents[driveKey];

        if (!rootContents) {
          try {
            const response = await sharepointAPI.getFolderContents(connection.id, drive.id);
            rootContents = response.data.items || [];
            setDriveContents(prev => ({ ...prev, [driveKey]: rootContents }));
          } catch (err) {
            console.error('Failed to load drive contents:', err);
            rootContents = [];
          }
        }

        // Get all root-level folders
        const rootFolders = rootContents.filter(item => item.folder);
        console.log(`[toggleSelection] Found ${rootFolders.length} root folders in drive`);

        // Collect each root folder and all its children
        for (const rootFolder of rootFolders) {
          // Add the root folder itself
          childrenToAdd.push({
            key: `${site.id}:${drive.id}:${rootFolder.id}`,
            siteId: site.id,
            siteName: site.displayName || site.name,
            driveId: drive.id,
            driveName: drive.name,
            folderId: rootFolder.id,
            folderName: rootFolder.name,
            folderPath: rootFolder.name,
          });

          // Update progress for root folder
          progressTracker.processed++;
          setProcessingProgress(progressTracker.processed);

          // Collect all children of this root folder
          const children = await collectAllChildFolders(site, drive, rootFolder, rootFolder.name, updateProgress, null, progressTracker);
          childrenToAdd.push(...children);
        }

        // Auto-expand the drive to show root folders
        setExpandedDrives(prev => ({ ...prev, [driveKey]: true }));
      }

      setProcessingTotal(childrenToAdd.length + 1);
      if (childrenToAdd.length > 0) {
        setProcessingMessage(`Selecting ${childrenToAdd.length + 1} folder${childrenToAdd.length > 0 ? 's' : ''}...`);
        // Small delay to show the count
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // Add this item and all its children to selectedItems
      setSelectedItems([...selectedItems, newItem, ...childrenToAdd]);
      setProcessingSelection(false);
      setProcessingMessage('');
      setProcessingProgress(0);
      setProcessingTotal(0);
    }
  };

  const isSelected = (site, drive, folder = null) => {
    const itemKey = folder
      ? `${site.id}:${drive.id}:${folder.id}`
      : `${site.id}:${drive.id}`;
    return selectedItems.some(item => item.key === itemKey);
  };

  const handleSaveSelection = async () => {
    if (selectedItems.length === 0) {
      setError('Please select at least one document library to save');
      return;
    }

    try {
      setSyncing(true);
      setError(null);

      // Transform selected items to match backend format
      // This includes all folders and their children
      const selectedSites = selectedItems.map(item => ({
        site_id: item.siteId,
        site_name: item.siteName,
        drive_id: item.driveId,
        drive_name: item.driveName,
        folder_id: item.folderId || null,
        folder_name: item.folderName || null,
        folder_path: item.folderPath || null,
      }));

      await sharepointAPI.saveSelection(connection.id, selectedSites);
      onComplete();
    } catch (err) {
      console.error('Failed to save selection:', err);
      setError(err.response?.data?.error || err.message);
      setSyncing(false);
    }
  };

  const handleStartSync = async () => {
    if (selectedItems.length === 0) {
      setError('Please select at least one document library to sync');
      return;
    }

    try {
      setSyncing(true);
      setError(null);

      // Transform selected items to match backend format
      // This includes all folders and their children
      const selectedSites = selectedItems.map(item => ({
        site_id: item.siteId,
        site_name: item.siteName,
        drive_id: item.driveId,
        drive_name: item.driveName,
        folder_id: item.folderId || null,
        folder_name: item.folderName || null,
        folder_path: item.folderPath || null,
      }));

      await sharepointAPI.startSync(connection.id, selectedSites);
      onComplete();
    } catch (err) {
      console.error('Failed to start sync:', err);
      setError(err.response?.data?.error || err.message);
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-6 relative">
      {/* Processing Overlay */}
      {(processingSelection || loadingPreviousSelections) && (
        <div className="absolute inset-0 bg-white bg-opacity-95 z-50 flex items-center justify-center rounded-lg">
          <div className="flex flex-col items-center gap-4 max-w-md w-full px-8">
            <FiLoader className="w-10 h-10 animate-spin text-blue-600" />
            <p className="text-base font-semibold text-gray-800 text-center">
              {loadingPreviousSelections ? 'Loading previous selections...' : processingMessage}
            </p>
            {processingTotal > 0 && !loadingPreviousSelections && (
              <div className="w-full">
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    className="bg-blue-600 h-3 rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${Math.min(100, (processingProgress / processingTotal) * 100)}%` }}
                  />
                </div>
                <p className="text-sm text-gray-600 mt-2 text-center">
                  {processingProgress} / {processingTotal} folders processed
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Browse SharePoint Sites</h2>
          <p className="text-sm text-gray-600 mt-1">
            Select the document libraries you want to sync from {connection.tenant_name}
          </p>
        </div>
        <button
          onClick={onCancel}
          className="text-gray-600 hover:text-gray-800"
        >
          <FiX className="w-6 h-6" />
        </button>
      </div>

      {/* Search */}
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Search for sites..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <button
          onClick={handleSearch}
          disabled={loading}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          Search
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <FiAlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Selected Items Summary */}
      {selectedItems.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-blue-900 mb-2">
            Selected Items ({selectedItems.length})
          </h3>
          <div className="space-y-1">
            {selectedItems.map((item) => (
              <div key={item.key} className="text-sm text-blue-800 flex items-center justify-between">
                <span>
                  {item.siteName} / {item.driveName}
                  {item.folderName && ` / ${item.folderName}`}
                </span>
                <button
                  onClick={() => setSelectedItems(selectedItems.filter(i => i.key !== item.key))}
                  className="text-blue-600 hover:text-blue-800"
                >
                  <FiX className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sites List */}
      <div className="border border-gray-200 rounded-lg overflow-hidden max-h-[500px] overflow-y-auto">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <FiLoader className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : sites.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <FiFolder className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p>No sites found</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {sites.map((site) => (
              <div key={site.id} className="bg-white">
                {/* Site Header */}
                <div
                  className="flex items-center p-4 hover:bg-gray-50 cursor-pointer"
                  onClick={() => toggleSite(site)}
                >
                  <div className="mr-2 text-gray-600">
                    {expandedSites[site.id] ? (
                      <FiChevronDown className="w-5 h-5" />
                    ) : (
                      <FiChevronRight className="w-5 h-5" />
                    )}
                  </div>
                  <FiFolder className="w-5 h-5 text-blue-600 mr-3" />
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">{site.displayName || site.name}</h3>
                    {site.description && (
                      <p className="text-sm text-gray-500">{site.description}</p>
                    )}
                  </div>
                </div>

                {/* Drives List */}
                {expandedSites[site.id] && (
                  <div className="bg-gray-50 border-t border-gray-200">
                    {loadingDrives[site.id] ? (
                      <div className="flex justify-center items-center py-8">
                        <FiLoader className="w-6 h-6 animate-spin text-blue-600" />
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-200">
                        {(siteDrives[site.id] || []).map((drive) => {
                          const driveKey = `${site.id}:${drive.id}`;
                          return (
                            <div key={drive.id}>
                              {/* Drive Header */}
                              <div className="flex items-center p-4 pl-12 hover:bg-gray-100">
                                <div
                                  className="mr-2 text-gray-600 cursor-pointer"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleDrive(drive, site);
                                  }}
                                >
                                  {expandedDrives[driveKey] ? (
                                    <FiChevronDown className="w-4 h-4" />
                                  ) : (
                                    <FiChevronRight className="w-4 h-4" />
                                  )}
                                </div>
                                <div
                                  className="flex items-center flex-1 cursor-pointer"
                                  onClick={() => toggleSelection(site, drive)}
                                >
                                  <div
                                    className={`w-5 h-5 mr-3 border-2 rounded flex items-center justify-center transition-colors ${
                                      isSelected(site, drive)
                                        ? 'bg-blue-600 border-blue-600'
                                        : 'border-gray-300'
                                    }`}
                                  >
                                    {isSelected(site, drive) && (
                                      <FiCheck className="w-3 h-3 text-white" />
                                    )}
                                  </div>
                                  <FiFolderPlus className="w-5 h-5 text-green-600 mr-3" />
                                  <div className="flex-1">
                                    <h4 className="font-medium text-gray-900">{drive.name}</h4>
                                    {drive.description && (
                                      <p className="text-sm text-gray-500">{drive.description}</p>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Folder Contents */}
                              {expandedDrives[driveKey] && (
                                <div className="bg-gray-100">
                                  {loadingFolders[driveKey] ? (
                                    <div className="flex justify-center items-center py-6">
                                      <FiLoader className="w-5 h-5 animate-spin text-blue-600" />
                                    </div>
                                  ) : (
                                    <div className="divide-y divide-gray-200">
                                      {(driveContents[driveKey] || [])
                                        .filter(item => item.folder)
                                        .map((folder) => (
                                          <FolderItem
                                            key={folder.id}
                                            folder={folder}
                                            drive={drive}
                                            site={site}
                                            level={2}
                                            onToggle={toggleFolder}
                                            onSelect={toggleSelection}
                                            isSelected={isSelected}
                                            isExpanded={expandedFolders}
                                            contents={driveContents}
                                            loading={loadingFolders}
                                            folderPath=""
                                          />
                                        ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <button
          onClick={onCancel}
          disabled={syncing}
          className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSaveSelection}
          disabled={syncing || selectedItems.length === 0}
          className="px-4 py-2 text-blue-700 bg-white border border-blue-600 rounded-lg hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center"
          title="Save selection for Graph search without syncing documents"
        >
          {syncing ? (
            <>
              <FiLoader className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              Save Selection ({selectedItems.length})
            </>
          )}
        </button>
        <button
          onClick={handleStartSync}
          disabled={syncing || selectedItems.length === 0}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center"
          title="Save selection and immediately sync all documents"
        >
          {syncing ? (
            <>
              <FiLoader className="w-4 h-4 mr-2 animate-spin" />
              Starting Sync...
            </>
          ) : (
            <>
              Save & Sync ({selectedItems.length})
            </>
          )}
        </button>
      </div>
    </div>
  );
}
